/**
 * Image preloading utility functions
 * 
 * Handles preloading of provider images (logos, headshots, hero images)
 * to improve UI performance and user experience. Prevents duplicate API calls
 * and manages image caching.
 */

import type { MappedMatch } from '../types/matchmaking.types';
import { fetchUserProfile, fetchProviderBase } from '../api/matchmakingApi';
import { getCachedImage } from './image-cache.utils';

/** UUID pattern to detect placeholder provider names like "Provider c03716e3-..." */
const UUID_PLACEHOLDER_PATTERN = /^Provider [0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isValidImageUrl = (url: string | null | undefined): url is string =>
  Boolean(url && url !== 'null' && url !== '');

/**
 * Hydrates provider matches with missing name and image data
 * 
 * For each match missing a logo or with a UUID-placeholder name:
 * 1. Tries /providers/:id/base for companyName + heroMediaUrl (best source)
 * 2. Falls back to /users/:id for profilePhoto + firstName/lastName
 * 
 * @param matches - Array of mapped matches
 * @returns Array of matches with resolved names and image URLs
 */
export const preloadProviderLogos = async (
  matches: MappedMatch[],
) => {
  const results = await Promise.all(
    matches.map(async (match) => {
      const providerProfile = match.providerProfile;
      if (!providerProfile) return match;

      const providerId = providerProfile.provider_id;
      if (!providerId) return match;

      const needsImage = !isValidImageUrl(providerProfile.logoOrHeadshot);
      const needsName = UUID_PLACEHOLDER_PATTERN.test(match.name);

      // Skip if both name and image are already resolved
      if (!needsImage && !needsName) return match;

      let resolvedImage: string | null = null;
      let resolvedName: string | null = null;

      // Primary source: /providers/:id/base (has companyName + heroMediaUrl)
      try {
        const baseData = await fetchProviderBase(providerId);
        if (needsName && baseData?.companyName) {
          resolvedName = baseData.companyName;
        }
        if (needsImage && isValidImageUrl(baseData?.heroMediaUrl)) {
          resolvedImage = baseData.heroMediaUrl!;
        }
        // Also try user.profilePhoto from provider base response
        if (needsImage && !resolvedImage && isValidImageUrl(baseData?.user?.profilePhoto)) {
          resolvedImage = baseData.user.profilePhoto;
        }
      } catch {
        // Provider base not available, try user profile fallback
      }

      // Fallback: /users/:id (has profilePhoto + firstName/lastName)
      if ((needsImage && !resolvedImage) || (needsName && !resolvedName)) {
        try {
          const userData = await fetchUserProfile(providerId);
          if (needsImage && !resolvedImage) {
            if (isValidImageUrl(userData?.logoOrHeadshot)) {
              resolvedImage = userData.logoOrHeadshot!;
            } else if (isValidImageUrl(userData?.profilePhoto)) {
              resolvedImage = userData.profilePhoto;
            }
          }
          if (needsName && !resolvedName && userData?.firstName) {
            resolvedName = [userData.firstName, userData.lastName].filter(Boolean).join(' ');
          }
        } catch {
          // Ignore fetch errors
        }
      }

      // Return updated match only if something changed
      if (!resolvedImage && !resolvedName) return match;

      return {
        ...match,
        ...(resolvedName && { name: resolvedName }),
        providerProfile: {
          ...providerProfile,
          ...(resolvedImage && { logoOrHeadshot: resolvedImage }),
        },
      };
    }),
  );
  return results;
};

/**
 * Tracks ongoing image preload requests to prevent duplicate API calls
 * Maps provider ID to the promise of the image URL fetch
 */
const ongoingPreloads = new Map<string, Promise<string | null>>();

/**
 * Preloads provider images for all matches
 * 
 * Fetches hero media URLs or logo/headshot images for providers.
 * Deduplicates provider IDs to avoid processing the same provider multiple times.
 * Uses existing preloaded images cache and tracks ongoing requests.
 * 
 * @param matches - Array of mapped matches
 * @param existingImages - Optional existing image cache
 * @returns Map of provider ID to image URL (or null if not found)
 */
export const preloadProviderImages = async (
  matches: MappedMatch[],
  existingImages?: Record<string, string | null>,
): Promise<Record<string, string | null>> => {
  const imageMap: Record<string, string | null> = {};
  
  // Deduplicate provider IDs to avoid processing the same provider multiple times
  const uniqueProviderIds = new Set<string>();
  const providerIdToMatch = new Map<string, MappedMatch>();
  
  matches.forEach((match) => {
    const providerId = match.providerProfile?.provider_id;
    if (providerId) {
      if (!uniqueProviderIds.has(providerId)) {
        uniqueProviderIds.add(providerId);
        providerIdToMatch.set(providerId, match);
      }
    }
  });
  
  await Promise.all(
    Array.from(uniqueProviderIds).map(async (providerId) => {
      // Check if image already exists in existing preloaded images
      if (existingImages && existingImages[providerId]) {
        imageMap[providerId] = existingImages[providerId];
        return;
      }
      
      // Check persistent cache
      const cachedImage = getCachedImage(providerId);
      if (cachedImage !== undefined) {
        imageMap[providerId] = cachedImage;
        return;
      }
      
      // Check if there's already an ongoing request for this provider
      if (ongoingPreloads.has(providerId)) {
        const existingPromise = ongoingPreloads.get(providerId);
        if (existingPromise) {
          imageMap[providerId] = await existingPromise;
          return;
        }
      }
      
      const match = providerIdToMatch.get(providerId);
      const providerProfile = match?.providerProfile;
      
      // If logoOrHeadshot already exists, use it and bind it to provider ID
      if (
        providerProfile?.logoOrHeadshot &&
        providerProfile.logoOrHeadshot !== 'null' &&
        providerProfile.logoOrHeadshot !== ''
      ) {
        imageMap[providerId] = providerProfile.logoOrHeadshot;
        return;
      }

      // Create a promise for this provider's image preload
      const preloadPromise = (async (): Promise<string | null> => {
        // Fetch from /providers/:id/base endpoint
        try {
          const data = await fetchProviderBase(providerId);
          if (data?.heroMediaUrl && data.heroMediaUrl !== 'null' && data.heroMediaUrl !== '') {
            return data.heroMediaUrl;
          }
        } catch {
          // Ignore fetch errors
        }
        
        // Fallback: try /users/:id endpoint
        try {
          const data = await fetchUserProfile(providerId);
          if (isValidImageUrl(data?.logoOrHeadshot)) {
            return data.logoOrHeadshot!;
          }
          // Also check profilePhoto as fallback (backend returns this field, not logoOrHeadshot)
          if (isValidImageUrl(data?.profilePhoto)) {
            return data.profilePhoto;
          }
        } catch {
          // Ignore fetch errors
        }
        
        return null;
      })();
      
      // Store the promise to prevent duplicate concurrent requests
      ongoingPreloads.set(providerId, preloadPromise);
      
      try {
        const imageUrl = await preloadPromise;
        // Always set the image (even if null) to ensure provider ID is in the map
        imageMap[providerId] = imageUrl;
        return imageUrl;
      } finally {
        // Clean up the ongoing request after completion
        ongoingPreloads.delete(providerId);
      }
    }),
  );
  
  // Ensure all provider IDs from matches are in the imageMap (even if null)
  matches.forEach((match) => {
    const providerId = match.providerProfile?.provider_id;
    if (providerId && !(providerId in imageMap)) {
      // If provider ID is not in map, set it to null to ensure binding exists
      imageMap[providerId] = null;
    }
  });
  
  return imageMap;
};

